import { useEffect, useState, UIEvent } from 'react';

import { useForm } from 'react-hook-form';
import slugify from 'slugify';

import { getUnformattedZipcode } from 'helpers';
import { useAddressByZipcode, useTracking } from 'hooks';

import type { AddressZipcodeResponse, ChangeAddress } from 'domains/change-cadastral';

export const useChangeAddressForm = () => {
  const MIN_LENGTH_ADDRESS = 5;
  const DEFAULT_LENGTH_ZIP_CODE = 8;
  const MAX_LENGTH_ZIP_CODE = 9;
  const [number, setNumber] = useState('');
  const [hasNoNumber, setHasNoNumber] = useState(false);
  const [isZipCodeInvalid, setIsZipCodeInvalid] = useState(false);
  const [isAddressInvalid, setIsAddressInvalid] = useState(false);
  const [isDisabledAddress, setIsDisabledAddress] = useState(true);
  const [isDisabledDistrict, setIsDisabledDistrict] = useState(true);
  const { register, handleSubmit, getValues, watch, setValue, formState } =
    useForm<ChangeAddress>();
  const zipcode = watch('zipcode') || '';
  const unformattedZipCode = getUnformattedZipcode(zipcode) || '';
  const addressLength = watch('address')?.length;
  const complementLength = watch('complement')?.length;
  const numbertLength = watch('number')?.length;
  const invalidNumber = number.startsWith('0');
  const { data, isSuccess, isError, error } = useAddressByZipcode(
    unformattedZipCode,
    unformattedZipCode?.length === DEFAULT_LENGTH_ZIP_CODE
  );

  const {
    data: addressData,
    detail,
    errorId,
    userMessage,
    success: isSuccessZipcode,
  } = (data as unknown as AddressZipcodeResponse) || {};

  const state = addressData?.state;
  const city = addressData?.city;
  const { sendDataToGTM } = useTracking();

  const handleTracking = (
    event: React.FocusEvent<HTMLInputElement> | UIEvent<HTMLButtonElement>
  ) => {
    const {
      currentTarget: { dataset },
    } = event;

    sendDataToGTM({
      event: 'analytics-event',
      event_parameters: {
        event_name: 'interaction_alterar_endereco',
        cd_interaction_detail: dataset.title,
      },
    });
  };

  const handleCallbackError = () => {
    const errorMessage =
      userMessage ?? detail ?? errorId ?? (error as any)?.data?.errorId ?? (error as any)?.message;
    const formattedMessage = slugify((errorMessage || '').toString(), {
      lower: true,
      replacement: '_',
      remove: /[*+~.()'"!:@/]/g,
    }).replace(/_/g, '-');

    sendDataToGTM({
      event: 'analytics-event',
      event_parameters: {
        event_name: 'callback_buscar_cep_erro',
        cd_error_message: formattedMessage,
      },
    });
  };

  const handleOnChangeNumber = (e: React.FormEvent<HTMLInputElement>) => {
    setNumber(e.currentTarget.value);
  };

  useEffect(() => {
    if (isSuccess && isSuccessZipcode) {
      setValue('city', `${addressData?.city}/${addressData?.state}`);
      setValue('address', addressData?.street ?? '');
      setValue('district', addressData?.district ?? '');
      setIsDisabledDistrict(Boolean(addressData?.district));
      setIsDisabledAddress(Boolean(addressData?.street));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuccess]);

  useEffect(() => {
    if (isError || (isSuccess && !isSuccessZipcode)) {
      handleCallbackError();

      setValue('city', '');
      setValue('address', '');
      setValue('district', '');
      setIsDisabledDistrict(true);
      setIsDisabledAddress(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isError, isSuccess, isSuccessZipcode]);

  const handleHasNoNumber = (event: any) => {
    setHasNoNumber(!hasNoNumber);
    setNumber('');
    handleTracking(event);
  };

  useEffect(() => {
    const subscription = watch((value) => {
      if (value.zipcode?.length !== MAX_LENGTH_ZIP_CODE) {
        setIsZipCodeInvalid(true);
      } else if ((value.address?.length ?? 0) < MIN_LENGTH_ADDRESS) {
        setIsAddressInvalid(true);
      } else {
        setIsZipCodeInvalid(false);
        setIsAddressInvalid(false);
      }
    });

    return () => subscription.unsubscribe();
  }, [watch]);

  return {
    canSubmit: !isAddressInvalid && !invalidNumber && isSuccess && isSuccessZipcode,
    handleTracking,
    handleSubmit,
    register,
    getValues,
    handleHasNoNumber,
    setNumber,
    handleOnChangeNumber,
    setValue,
    isSuccess: isSuccess && isSuccessZipcode,
    isError: isError || (isSuccess && !isSuccessZipcode),
    formState,
    addressLength,
    complementLength,
    hasNoNumber,
    number,
    numbertLength,
    invalidNumber,
    isZipCodeInvalid,
    isAddressInvalid,
    isDisabledAddress,
    isDisabledDistrict,
    zipcode,
    unformattedZipCode,
    state,
    city,
    addressData,
  };
};
